// The API's shapes and one fetch wrapper. Cookies carry the session; a 401 means "sign in".

export type Role = "viewer" | "dev" | "admin";
export type Stage = "build" | "demo" | "pilot" | "released" | "handed-off" | "retired";
export const STAGES: Stage[] = ["build", "demo", "pilot", "released", "handed-off", "retired"];

export interface Me {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  role: Role;
}

export interface StackState {
  stackName: string;
  region: string;
  status: string;
  updatedAt: string | null;
  outputs: Record<string, string>;
}

export interface ServiceInfo {
  name: string;
  description?: string;
  image?: string;
  ports?: string[];
}

export interface InstanceState {
  name: string;
  id: string | null;
  state: string;
  type: string | null;
  publicIp: string | null;
  launchedAt: string | null;
}

export interface ClusterState {
  name: string;
  status: string;
  runningTasks: number;
  services: number;
}

export type Health = "up" | "partly-up" | "stopped" | "down" | "changing" | "not-deployed" | "unknown";

export interface Env {
  onLab: boolean;
  containers: { service: string; name: string; state: string; status: string; image: string }[];
  health: Health;
  healthReasons?: string[];
  instances: InstanceState[];
  clusters: ClusterState[];
  id: string;
  name: string;
  purpose: string;
  region: string;
  kind: "app" | "external";
  status: string;
  urls: string[];
  checks: string[]; // health checks: "https://…/_health" or "…=204"
  services: ServiceInfo[];
  ttl: string | null;
  lastShipSha: string | null;
  lastShipAt: string | null;
  sharedDb: boolean;
  instanceType: string | null;
  stacks: StackState[];
}

export interface ProjectSummary {
  id: string;
  slug: string;
  name: string;
  description: string;
  client: string;
  stage: Stage;
  repo: string;
  contact: string;
  owner: string | null;
  updatedAt: string;
  environments: Env[];
}

export interface Project extends Omit<ProjectSummary, "environments" | "updatedAt"> {
  notes: string;
  tags: string[];
  aws_tags: string[];
  costPerMonth: number | null;
  credentialCount: number;
  cost_plan: string;
  owner_id: string | null;
  ownerName: string | null;
  created_at: string;
  updated_at: string;
  environments: Env[];
}

export interface OverviewEnv extends Env {
  project: string;
  projectName: string;
  stage: Stage;
  client: string;
  owner: string | null;
}

export interface Attention {
  kind: "down" | "stopped" | "expiring" | "expired" | "approval";
  project: string;
  projectName: string;
  env: string;
  text: string;
}

export interface Overview {
  checkedAt: string;
  totals: { projects: number; environments: number; up: number; partlyUp: number; stopped: number; down: number; notDeployed: number; boxes: number };
  byStage: Partial<Record<Stage, number>>;
  attention: Attention[];
  environments: OverviewEnv[];
}

export interface UserRow {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  role: Role;
  lastLoginAt: string | null;
}

export interface AuditRow {
  id: string;
  at: string;
  action: string;
  target: string;
  detail: Record<string, unknown>;
  actor: string | null;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: { ...(init.body ? { "content-type": "application/json" } : {}), ...init.headers },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = Array.isArray(body?.message) ? body.message.join(", ") : body?.message ?? res.statusText;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

export const rank: Record<Role, number> = { viewer: 0, dev: 1, admin: 2 };
export const can = (me: Me | undefined, role: Role) => Boolean(me && rank[me.role] >= rank[role]);

/* ---------------- docs ---------------- */

export type DocAudience = "end-user" | "admin" | "developer";
export type DocLanguage = "ar" | "en";
export type DocLinkKind = "video" | "user-guide" | "wiki" | "design" | "api-docs" | "other";
export const DOC_AUDIENCES: DocAudience[] = ["end-user", "admin", "developer"];
export const DOC_LINK_KINDS: DocLinkKind[] = ["video", "user-guide", "wiki", "design", "api-docs", "other"];

export interface DocPageSummary {
  id: string;
  slug: string;
  title: string;
  audience: DocAudience;
  language: DocLanguage;
  env: string | null;
  isGuide: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}
export interface DocPage extends DocPageSummary { body: string }

export interface DocLink {
  id: string;
  title: string;
  url: string;
  kind: DocLinkKind;
  audience: DocAudience;
  language: DocLanguage;
  notes: string;
  embedUrl: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface DocFile {
  id: string;
  title: string;
  filename: string;
  contentType: string;
  kind: "video" | "pdf" | "image" | null;
  sizeBytes: number;
  audience: DocAudience;
  language: DocLanguage;
  notes: string;
  status: "pending" | "ready";
  updatedAt: string;
  uploadedBy: string | null;
}

export interface ProjectDocs {
  mayEdit: boolean;
  uploads: { enabled: boolean; maxBytes: number };
  pages: DocPageSummary[];
  links: DocLink[];
  files: DocFile[];
}

export interface DocRevision { id: string; title: string; language: DocLanguage; editedAt: string; editedBy: string | null; length: number }
